import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useGlobalStore } from '@/stores';
import { StanButton } from '@/components';
import { ButtonColor, ButtonVariant } from '@/components/core-components/types';
import { useStanRouter } from '@/hooks/useStanRouter';

type ComponentStatus = 'done' | 'not-done';

interface ComponentInfo {
    name: string;
    status: ComponentStatus;
    route?: string; // For components that have their own pages
    sectionId?: string; // For components that are sections on the main page
}

const COMPONENTS: ComponentInfo[] = [
    // Done components (available in current page)
    { name: 'StanButton', status: 'done', sectionId: 'buttons' },
    { name: 'StanInput', status: 'done', sectionId: 'inputs' },
    { name: 'StanFileUpload', status: 'done', sectionId: 'file-upload' },
    { name: 'StanCropComponent', status: 'done', sectionId: 'modals' },
    { name: 'StanDropdown', status: 'done', sectionId: 'buttons' },
    { name: 'StanAccordion', status: 'done', sectionId: 'accordions' },
    { name: 'StanModal', status: 'done', sectionId: 'modals' },
    { name: 'StanTable', status: 'done', sectionId: 'tables' },
    { name: 'StanTooltip', status: 'done', sectionId: 'tooltips' },
    { name: 'StanTextArea', status: 'done', sectionId: 'textareas' },
    { name: 'StanTag', status: 'done', sectionId: 'tags' },
    { name: 'StanDivider', status: 'done', sectionId: 'dividers' },
    { name: 'StanCalendar', status: 'done', route: '/internals/calendar' },
    { name: 'StanDataRow', status: 'done', route: '/internals/data-row' },
    { name: 'StanTipTapEditor', status: 'done', route: '/internals/tiptap-editor' },
    { name: 'StanBadge', status: 'done', sectionId: 'badges' },
    { name: 'StanCheckbox', status: 'done', sectionId: 'checkbox' },
    { name: 'StanSegmentedControl', status: 'done', route: '/internals/segmented-control' },
    { name: 'StanTabs', status: 'done', route: '/internals/tabs' },
    { name: 'NavigationConfirmation', status: 'done', route: '/internals/navigation-confirmation' },
    { name: 'Stan File Upload', status: 'done', route: '/internals/unsplash-image-selector' },
    { name: 'Stan Product Editor', status: 'done', route: '/internals/product-editor' },
    { name: 'StanBanner', status: 'done', sectionId: 'stanbanner' },

    // Not done components (in progress, awaiting approval, or not started)
    { name: 'StanToggle', status: 'not-done', sectionId: 'stantoggle' },
    { name: 'StanPhoneInput', status: 'not-done', sectionId: 'stanphoneinput' },
    { name: 'StanFilter', status: 'not-done', sectionId: 'stanfilter' },
    { name: 'StanBarChart', status: 'not-done', sectionId: 'stanbarchart' },
    { name: 'StanLineChart', status: 'not-done', sectionId: 'stanlinechart' },
    { name: 'StanMultiSelect', status: 'not-done', sectionId: 'stanmultiselect' },
    { name: 'StanPageTabs', status: 'not-done', sectionId: 'stanpagetabs' },
    { name: 'StanTimeZone', status: 'not-done', sectionId: 'stantimezone' },
    { name: 'LoadingSkeletons', status: 'not-done', sectionId: 'loadingskeletons' },
    { name: 'AnalyticsCard', status: 'not-done', sectionId: 'analyticscard' },
];

export default function InternalsLayout() {
    const setBreadcrumbs = useGlobalStore(state => state.setBreadcrumbs);
    const darkMode = useGlobalStore(state => state.darkMode);
    const setDarkMode = useGlobalStore(state => state.setDarkMode);
    const { push } = useStanRouter();
    const location = useLocation();

    // Set breadcrumbs for internals section
    useEffect(() => {
        setBreadcrumbs([
            { title: 'Home', to: '/dashboard', id: 'home' },
            { title: 'Internals', to: '/internals', active: true, id: 'internals' },
        ]);
    }, [setBreadcrumbs]);

    // Smooth scroll to component section (for main internals page)
    const scrollToComponent = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    };

    // Handle component navigation
    const handleComponentClick = (component: ComponentInfo) => {
        if (component.route) {
            push(component.route);
        } else if (component.sectionId) {
            // If we're already on the internals page, just scroll
            if (location.pathname === '/internals') {
                scrollToComponent(component.sectionId);
            } else {
                // Navigate to main internals page first, then scroll
                push('/internals');
                // Use a longer timeout to ensure the page has loaded
                setTimeout(() => {
                    if (component.sectionId) {
                        scrollToComponent(component.sectionId);
                    }
                }, 300);
            }
        }
    };

    // Get status styling for component buttons
    const getStatusStyling = (status: ComponentStatus) => {
        switch (status) {
            case 'done':
                return {
                    variant: 'pill' as ButtonVariant,
                    color: 'primary' as ButtonColor,
                    disabled: false,
                };
            case 'not-done':
                return {
                    variant: 'ghost' as ButtonVariant,
                    color: 'gray' as ButtonColor,
                    disabled: true,
                };
            default:
                return {
                    variant: 'ghost' as ButtonVariant,
                    color: 'gray' as ButtonColor,
                    disabled: true,
                };
        }
    };

    return (
        <div className='min-h-svh py-8'>
            <div className='w-full'>
                <div className='mb-12 text-center'>
                    <div className='mb-6 flex flex-col items-center justify-center gap-4'>
                        <h1 className='text-4xl font-bold' style={{ color: 'var(--text-color-dark)' }}>
                            Component Playground
                        </h1>
                        <div className='flex items-center gap-2'>
                            <span className='text-sm' style={{ color: 'var(--text-color-gray)' }}>
                                Theme:
                            </span>
                            <StanButton
                                name='theme-toggle'
                                variant='outline'
                                size='xs'
                                onClick={() => setDarkMode(!darkMode)}
                                type='button'
                            >
                                {darkMode ? '🌙 Dark' : '☀️ Light'}
                            </StanButton>
                        </div>
                    </div>
                    <p className='text-lg' style={{ color: 'var(--text-color-gray)' }}>
                        Interactive testing environment for Stan UI components
                    </p>
                </div>

                {/* Component Navigation */}
                <div className='mb-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
                    <div className='mb-4'>
                        <h2 className='text-xl font-semibold text-gray-900'>Component Navigation</h2>
                        <p className='mt-1 text-sm text-gray-600'>
                            Click on a component to jump to its section. Disabled components are not yet implemented.
                        </p>
                    </div>
                    <div className='flex flex-wrap gap-2'>
                        {COMPONENTS.map(component => {
                            const styling = getStatusStyling(component.status);
                            return (
                                <StanButton
                                    key={component.name}
                                    name={`nav-${component.name.toLowerCase()}`}
                                    variant={styling.variant}
                                    color={styling.color}
                                    size='xs'
                                    disabled={styling.disabled}
                                    onClick={() => handleComponentClick(component)}
                                    type='button'
                                >
                                    {component.name}
                                </StanButton>
                            );
                        })}
                    </div>
                    <div className='mt-4 flex flex-wrap gap-4 text-xs text-gray-500'>
                        <div className='mt-4 flex flex-wrap gap-4 text-xs' style={{ color: 'var(--text-color-gray)' }}>
                            <div className='flex items-center gap-1'>
                                <div
                                    className='h-2 w-2 rounded-full'
                                    style={{ backgroundColor: 'var(--color-brand-main)' }}
                                />
                                <span>Done - Ready to use</span>
                            </div>
                            <div className='flex items-center gap-1'>
                                <div
                                    className='h-2 w-2 rounded-full'
                                    style={{ backgroundColor: 'var(--color-neutral-subtle)' }}
                                />
                                <span>Not Done - In development</span>
                            </div>
                        </div>
                    </div>
                </div>

                <hr />

                {/* Page Content */}
                <Outlet />
            </div>
        </div>
    );
}
